import { Link } from "react-router-dom";
import ContentPage, { Eyebrow, PageTitle, Lead, H2, P } from "@/components/ContentPage";
import { useSeo } from "@/lib/seo";

export default function PlanningYoung() {
  useSeo({
    title: "Financial Planning in Your 20s and 30s | Build a Strong Financial Foundation",
    description: "Learn the key financial planning areas to consider in your 20s and 30s, including budgeting, emergency savings, debt, investing, taxes, and protection.",
    canonicalPath: "/financial-planning-20s-30s",
  });
  return (
    <ContentPage
      cta="Take the Free Assessment"
      breadcrumbs={[
        { to: "/", label: "Home" },
        { to: "/financial-planning-20s-30s", label: "Financial Planning in Your 20s & 30s" },
      ]}
      readMinutes={7}
    >
      <Eyebrow>Guide · 20s &amp; 30s</Eyebrow>
      <PageTitle>Building Your Financial Foundation in Your 20s and 30s</PageTitle>
      <Lead>
        Your 20s and 30s are the years when consistent habits often matter more than the specific investments you pick.
        The goal is a healthy foundation: awareness of where money goes, room for the unexpected, and a durable pattern of saving and investing.
      </Lead>

      <H2>Know where your money goes</H2>
      <P>
        Cash-flow awareness is one of the most useful skills at this stage. Knowing your approximate monthly spending — housing,
        transportation, food, insurance, debt payments, and everyday expenses — makes every other financial decision easier: how much to save,
        how much liquidity to hold, and how much you can commit to long-term goals without straining daily life.
      </P>

      <H2>Build emergency flexibility</H2>
      <P>
        A cash cushion helps absorb surprises without borrowing at unfavorable terms or selling long-term investments at the wrong time.
        There is no single "correct" number of months to hold in reserve for everyone; your job stability, family situation, insurance coverage,
        and comfort with risk all play a role. What matters most is that some cushion exists and it is genuinely accessible.
      </P>

      <H2>Understand your debt</H2>
      <P>
        Not all debt behaves the same way. A reasonable mortgage or student loan is not automatically a problem. The question worth asking is
        whether debt payments are limiting your ability to save, invest, or make progress toward other financial goals. High-interest revolving
        balances — credit cards carried month to month — tend to reduce flexibility the fastest.
      </P>

      <H2>Start saving and investing consistently</H2>
      <P>
        Building the habit of setting money aside — even modestly and imperfectly — tends to matter more in your 20s and 30s than picking the
        "perfect" investment. Consistency benefits from time. This guide does not recommend specific investments; it simply suggests that a
        defined approach and a regular contribution rhythm are worth having.
      </P>

      <H2>Understand account types</H2>
      <P>
        Different accounts behave differently for taxes and access:
      </P>
      <ul className="mt-3 pl-6 list-disc space-y-1.5 text-base" style={{ color: "#17233D", lineHeight: 1.65 }}>
        <li><strong>Checking, savings, and money market accounts</strong> for near-term spending and liquidity.</li>
        <li><strong>Workplace retirement plans</strong> such as 401(k), 403(b), or 457.</li>
        <li><strong>Traditional retirement accounts</strong> like Traditional IRAs — generally pre-tax contributions.</li>
        <li><strong>Roth accounts</strong> — after-tax contributions with different withdrawal rules.</li>
        <li><strong>Taxable brokerage accounts</strong> — no special tax treatment but full flexibility.</li>
      </ul>
      <P>Understanding these categories helps you make more intentional decisions rather than defaulting to whichever account is easiest to open.</P>

      <H2>Think about taxes early</H2>
      <P>
        Tax planning is not only about April. Where you put money — pre-tax, after-tax, or taxable — can influence what your future
        options look like. No single tax treatment is universally better; the point of thinking about it early is simply to keep options open.
      </P>

      <H2>Protect your income</H2>
      <P>
        Consider what would happen if income stopped unexpectedly — through illness, disability, or another disruption. Emergency reserves,
        appropriate insurance coverage, and other protection considerations belong in a financial-foundation conversation, not just a retirement one.
      </P>

      <H2>Basic estate planning matters earlier than many people think</H2>
      <P>
        Even in your 20s and 30s it can be worth confirming that beneficiary designations on your accounts are current and that basic documents —
        a will, a financial power of attorney, and a healthcare directive — exist and reflect your intentions. A trust is not necessary for every household.
      </P>

      <P className="mt-10">
        Not sure where you stand? <Link to="/assessment" className="underline" style={{ color: "#258FC4" }}>Take the Financial Future Readiness Assessment</Link>.
      </P>
    </ContentPage>
  );
}
